import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { PartSearch, Refinement } from 'entities/part-search';
import { PartSearchResponse } from 'entities/parts/part-search-response';
import { ToastType } from 'entities/toast-type';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { SourceLocationType } from 'services/logger.service';
import { LoyaltyService } from 'services/loyalty.service';
import { PartService } from 'services/part.service';
import { ToastService } from 'services/toast.service';
import { TrackerService } from 'services/tracker.service';
import { AppState } from 'store/app-state';
import * as BranchSelectors from 'store/branch/branch.selectors';
import * as CustomerSelectors from 'store/customer/customer.selectors';
import * as LoyaltySelectors from 'store/loyalty/loyalty.selectors';

interface QueryState {
  term: string;
  partId: string;
  isExpandedSearch: boolean;
  facets: Refinement[];
  pageNumber: number;
}

interface PartBasePortableComponentState {
  query: QueryState;
  result: PartSearchResponse;
  loading: boolean;
  dismissedCreatePart: boolean;
}

@Injectable()
export class PartBasePortableComponentStore extends ComponentStore<PartBasePortableComponentState> implements OnDestroy {
  public readonly pageSize = 20;

  private readonly querySubject = new BehaviorSubject<QueryState>({
    term: '',
    partId: '',
    isExpandedSearch: false,
    facets: [],
    pageNumber: 1
  });

  // Use a separate subject to trigger searches
  // This ensures we have control over when searches are triggered (to avoid issues like #199109)
  private readonly searchTrigger$ = new Subject<{ query: QueryState; source: 'query' | 'branch' | 'customer' }>();
  private lastSearchedBranchCode: string | null = null;
  // Cache the last successful customer for context-triggered searches (to avoid issues like #199134)
  // (the store selector may return null during branch/customer change transitions)
  private lastSearchedCustomerRewardNumber: string | null = null;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private partService: PartService,
    private toastService: ToastService,
    private trackerService: TrackerService,
    private loyaltyService: LoyaltyService
  ) {
    super({
      query: {
        term: '',
        partId: '',
        isExpandedSearch: false,
        facets: [],
        pageNumber: 1
      },
      result: null,
      loading: false,
      dismissedCreatePart: false
    });

    // Set up a SINGLE search subscription that handles both
    // query-triggered and branch-triggered searches (to better deal with issues like #199109 and #199134)
    this.setupUnifiedSearchSubscription();
  }

  // Single subscription that handles all search triggers
  private setupUnifiedSearchSubscription(): void {
    this.searchTrigger$.pipe(
      takeUntil(this.destroy$),
      // For query-triggered searches on page load, wait for branch and customer to be available
      // This prevents the race condition where queryParams emit before store state is loaded
      switchMap((trigger) => {
        if (trigger.source === 'query') {
          // Wait for both branch and customer to be available before proceeding
          return combineLatest([
            this.store.select(BranchSelectors.selectedBranch).pipe(
              filter(branch => Boolean(branch?.code)),
              first()
            ),
            this.store.select(CustomerSelectors.selectedCustomer).pipe(
              filter(customer => Boolean(customer?.rewardNumber)),
              first()
            )
          ]).pipe(
            map(([branch, customer]) => ({ trigger, branch, customer }))
          );
        } else {
          // For branch/customer triggers, use withLatestFrom behavior
          return combineLatest([
            this.store.select(BranchSelectors.selectedBranch).pipe(first()),
            this.store.select(CustomerSelectors.selectedCustomer).pipe(first())
          ]).pipe(
            map(([branch, customer]) => ({ trigger, branch, customer }))
          );
        }
      }),
      // For branch/customer-triggered searches, allow cached values if store values are null
      filter(({ trigger, branch, customer }) => {
        const hasBranch = Boolean(branch?.code);
        const hasCustomer = Boolean(customer?.rewardNumber);
        const hasCachedBranch = Boolean(this.lastSearchedBranchCode);
        const hasCachedCustomer = Boolean(this.lastSearchedCustomerRewardNumber);

        if (trigger.source === 'branch') {
          // For branch triggers, we can use the cached customer
          return hasBranch && (hasCustomer || hasCachedCustomer);
        }
        if (trigger.source === 'customer') {
          // For customer triggers, we can use the cached branch
          return hasCustomer && (hasBranch || hasCachedBranch);
        }
        // For query triggers, we already waited for both to be available
        return hasBranch && hasCustomer;
      }),
      // For context-triggered searches, skip if the context hasn't actually changed
      filter(({ trigger, branch, customer }) => {
        if (trigger.source === 'branch') {
          if (this.lastSearchedBranchCode === branch.code) {
            return false; // Skip - branch hasn't changed since last search
          }
        }
        if (trigger.source === 'customer') {
          const rewardNumber = customer?.rewardNumber;
          if (this.lastSearchedCustomerRewardNumber === rewardNumber) {
            return false; // Skip - customer hasn't changed since last search
          }
        }
        return true;
      }),
      tap(() => {
        this.patchState({
          loading: true,
          dismissedCreatePart: false
        });
      }),
      switchMap(({ trigger, branch, customer }) => {
        const query = trigger.query;
        // Use values from store if available, otherwise use cached values
        const branchCode = branch?.code || this.lastSearchedBranchCode;
        const rewardNumber = customer?.rewardNumber || this.lastSearchedCustomerRewardNumber;
        const partSearch: PartSearch = {
          branchCode: branchCode,
          rewardNumber: rewardNumber,
          partSearchTerm: query.term,
          partId: query.partId,
          isExpandedSearch: query.isExpandedSearch,
          isCountCheck: false,
          refinements: query.facets,
          pageNumber: query.pageNumber,
          pageSize: this.pageSize
        };

        // Track the branch code and customer for this search
        this.lastSearchedBranchCode = branch.code;
        this.lastSearchedCustomerRewardNumber = rewardNumber;

        return this.partService.searchParts(partSearch).pipe(
          tap((response) => {
            if (response.ErrorType && response.ErrorType !== 200) {
              throw new Error(response);
            }
          }),
          map(result => ({ result, branch, partSearch, query })),
          withLatestFrom(this.store.select(LoyaltySelectors.loyaltyAccount)),
          switchMap(([data, loyaltyAccount]) => {
            if (loyaltyAccount) {
              return this.loyaltyService.getDiscounts({
                memberId: loyaltyAccount.loyaltyAccountIdentifier,
                partNumbers: data.result?.parts?.map(x => x.rushPartNumber),
                branchCode: data.branch.code,
                loyaltyProgram: loyaltyAccount.loyaltyProgram
              }).pipe(
                map((discounts) => {
                  data.result.parts = data.result.parts.map(part => {
                    const discount: any = discounts[part.rushPartNumber.split(":")[0]];
                    if (discount) {
                      const loyaltyProgram = loyaltyAccount.loyaltyProgram;
                      part.sapCoupons = [
                        ...part.sapCoupons,
                        {
                          amount: discount.amount,
                          description: discount.code + " " + loyaltyProgram.charAt(0).toUpperCase() + loyaltyProgram.slice(1),
                          type: 'part'
                        }
                      ];
                    }
                    return part;
                  });
                  return data;
                })
              );
            }
            return of(data);
          })
        );
      })
    ).subscribe({
      next: (data) => {
        const { result, partSearch, query } = data;
        const source = SourceLocationType[SourceLocationType.PartListResult];
        this.trackerService.trackPartSearch(result.parts, partSearch.partSearchTerm, source);

        // Only show "Part not found" on the first page of a search
        // When paginating (page > 1), empty results just means we've reached the end
        const isFirstPage = partSearch.pageNumber === 1;
        if (isFirstPage && (!result || result.totalParts <= 0)) {
          this.toastService.showToast('Part not found.', ToastType.Error);
        }
        if (result && result.totalParts > 1 && result.isExpandedSearch) {
          this.toastService.showToast('Your results contain parts from an expanded industry search', ToastType.Success);
        }

        // If we're paginating and there are no more results, don't update the result
        // This preserves the existing parts list in the UI
        if (!isFirstPage && (!result || result.totalParts <= 0 || result.parts?.length === 0)) {
          this.patchState({ loading: false });
          return;
        }

        this.setState((state) => ({
          ...state,
          query: {
            ...state.query,
            term: query.term,
            partId: query.partId,
            isExpandedSearch: result.isExpandedSearch,
            pageNumber: partSearch.pageNumber
          },
          result,
          loading: false,
          dismissedCreatePart: false
        }));

        // Only update route if not in modal
        if (this.router.url.includes('/parts')) {
          this.router.navigate(['/parts'], {
            queryParams: {
              isExpandedSearch: result.isExpandedSearch,
              page: partSearch.pageNumber
            },
            queryParamsHandling: "merge",
            onSameUrlNavigation: "ignore"
          });
        }
      },
      error: (error) => {
        this.toastService.errorMessage('PartBaseComponent', 'getParts', 'getParts', null);
        this.patchState({ loading: false, dismissedCreatePart: false });
      }
    });
  }

  // Set up branch change listener after initial search (to account for issues like #199134)
  private setupBranchChangeListener(): void {
    this.store.select(BranchSelectors.selectedBranch).pipe(
      takeUntil(this.destroy$),
      filter(branch => Boolean(branch?.code)),
      map(branch => branch.code),
      distinctUntilChanged(),
      // Add a small delay to allow navigation to complete first
      // This prevents the search from triggering when navigating away from /parts (i.e. issue #199109)
      debounceTime(50)
    ).subscribe(branchCode => {
      if (!this.router.url.includes('/parts')) {
        return;
      }
      // Only trigger if branch has changed since last search
      if (this.lastSearchedBranchCode !== null && this.lastSearchedBranchCode !== branchCode) {
        const currentQuery = this.get(state => state.query);
        if (currentQuery.term || currentQuery.partId) {
          this.searchTrigger$.next({ query: currentQuery, source: 'branch' });
        }
      }
    });
  }

  // Set up customer change listener to refresh pricing when customer changes
  private setupCustomerChangeListener(): void {
    this.store.select(CustomerSelectors.selectedCustomer).pipe(
      takeUntil(this.destroy$),
      filter(customer => Boolean(customer?.rewardNumber)),
      map(customer => customer.rewardNumber),
      distinctUntilChanged(),
      // Add a small delay to allow navigation to complete first
      // This prevents the search from triggering when navigating away from /parts
      debounceTime(50)
    ).subscribe(rewardNumber => {
      if (!this.router.url.includes('/parts')) {
        return;
      }
      // Only trigger if customer has changed since last search
      if (this.lastSearchedCustomerRewardNumber !== null && this.lastSearchedCustomerRewardNumber !== rewardNumber) {
        const currentQuery = this.get(state => state.query);
        if (currentQuery.term || currentQuery.partId) {
          this.searchTrigger$.next({ query: currentQuery, source: 'customer' });
        }
      }
    });
  }

  public init(isInModal: boolean, initialSearchTerm?: string, initialPartId?: string, activatedRoute?: ActivatedRoute, initialPageNumber?: number, branchCode?: string, rewardNumber?: string): void {
    if (isInModal) {
      // Modal mode: use initial inputs directly
      const initialQuery: QueryState = {
        term: initialSearchTerm || '',
        partId: initialPartId || '',
        isExpandedSearch: false,
        facets: [],
        pageNumber: initialPageNumber || 1
      };

      this.querySubject.next(initialQuery);
      // Use the unified search trigger
      this.searchTrigger$.next({ query: initialQuery, source: 'query' });
    } else {
      // Route mode: watch query params from activated route
      this.initFromRoute(activatedRoute);
      // Set up branch and customer change listeners for non-modal mode
      this.setupBranchChangeListener();
      this.setupCustomerChangeListener();
    }
  }

  private initFromRoute(activatedRoute: ActivatedRoute): void {
    activatedRoute.queryParams
      .pipe(
        takeUntil(this.destroy$),
        map((queryParams) => ({
          term: queryParams['searchTerm'] || '',
          partId: queryParams['partId'] || '',
          isExpandedSearch: queryParams['isExpandedSearch'] === 'true',
          pageNumber: Number(queryParams['page']) || 1
        })),
        filter((queryParams) => {
          const queryState = this.get((state) => state.query);
          return queryState.term !== queryParams.term
            || queryState.partId !== queryParams.partId
            || queryState.isExpandedSearch !== queryParams.isExpandedSearch
            || queryState.pageNumber !== queryParams.pageNumber;
        }),
        map((queryParams): QueryState => {
          const currentState = this.get((state) => state.query);
          // Preserve facets when only pagination changes (same search term/partId)
          // Reset facets when search term or partId changes (new search)
          const isNewSearch = currentState.term !== queryParams.term || currentState.partId !== queryParams.partId;
          return {
            ...queryParams,
            facets: isNewSearch ? [] : currentState.facets
          };
        })
      )
      .subscribe(query => {
        this.querySubject.next(query);
        // Use the unified search trigger
        this.searchTrigger$.next({ query, source: 'query' });
      });
  }

  public readonly updatePage = this.updater((state, page: number) => {
    const newQuery = {
      ...state.query,
      pageNumber: page
    };
    this.querySubject.next(newQuery);
    // Use the unified search trigger
    this.searchTrigger$.next({ query: newQuery, source: 'query' });
    return {
      ...state,
      query: newQuery
    };
  });

  public readonly updateFacets = this.effect((facets$: Observable<Refinement[]>) =>
    facets$
      .pipe(
        tap((newFacets) => {
          const newQuery = {
            ...this.get((state) => state.query),
            facets: newFacets,
            pageNumber: 1
          };
          this.patchState({
            query: newQuery
          });
          this.querySubject.next(newQuery);
          // Use the unified search trigger
          this.searchTrigger$.next({ query: newQuery, source: 'query' });
        }),
        // Complete the effect by returning an empty observable
        switchMap(() => of(void 0))
      )
  );

  public readonly navigateToPartDetails = this.updater((state, partId: string) => {
    const newQuery = {
      ...state.query,
      partId: partId,
      pageNumber: 1
    };
    this.querySubject.next(newQuery);
    // Use the unified search trigger
    this.searchTrigger$.next({ query: newQuery, source: 'query' });
    return {
      ...state,
      query: newQuery
    };
  });

  public readonly searchForPart = this.updater((state, params: { term: string, partId: string }) => {
    const newQuery = {
      ...state.query,
      term: params.term,
      partId: params.partId,
      isExpandedSearch: false,
      facets: [],
      pageNumber: 1
    };
    this.querySubject.next(newQuery);
    // Use the unified search trigger
    this.searchTrigger$.next({ query: newQuery, source: 'query' });
    return {
      ...state,
      query: newQuery
    };
  });

  public readonly clearFacets = this.updater((state) => ({
    ...state,
    query: {
      ...state.query,
      facets: []
    },
  }));

  public readonly dismissCreatePart = this.updater((state) => ({
    ...state,
    dismissedCreatePart: true
  }));

  private readonly query$ = this.select((state) => state.query);
  public readonly queryTerm$ = this.select(this.query$, (query) => query.term);
  public readonly pageNumber$ = this.select(this.query$, (query) => query.pageNumber);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly result$ = this.select((state) => state.result);
  public readonly count$ = this.select(this.result$, (result) => result?.totalParts ?? 0);
  public readonly showCreatePart$ = this.select((state) => !state.loading && !state.result?.totalParts && !state.dismissedCreatePart);
  public readonly facets$ = this.select(this.query$, (query) => query.facets);

  override ngOnDestroy(): void {
    this.querySubject.complete();
    this.searchTrigger$.complete();
    super.ngOnDestroy(); // This handles destroy$ automatically
  }
}
